import { NavLink, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { ROLE_LABEL } from "../lib/constants";
import NotificationTray from "./NotificationTray";
import HelpButton from "./HelpButton";
import CommandPalette from "./CommandPalette";
import WorkspaceBanners from "./WorkspaceBanners";
import { InstallAppButton, PushToggleButton } from "./PwaInstall";
import {
  KeyRound, Bot,
  LayoutDashboard, Columns3, Building2, Inbox, ListChecks,
  Globe, Gauge, ScrollText, LogOut, Radio, Radar, Upload, SlidersHorizontal, CalendarClock, BookOpen, Linkedin, BarChart3, CalendarHeart, Users, UserMinus, Store, Network, Target, Search as SearchIcon, ShieldCheck, CheckSquare, HelpCircle, FileText, Rocket,
  Menu, X, Sun, Moon, PanelLeftClose, PanelLeftOpen, Mail as MailIcon,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Command Center", icon: LayoutDashboard, roles: ["admin", "supervisor", "rep"] },
  { to: "/pipeline", label: "Pipeline", icon: Columns3, roles: ["admin", "supervisor", "rep"] },
  { to: "/productivity", label: "Productivity", icon: BarChart3, roles: ["admin", "supervisor", "rep"] },
  { to: "/targets", label: "Target Setting", icon: Target, roles: ["admin", "supervisor", "rep"] },
  { to: "/queue", label: "My Queue", icon: ListChecks, roles: ["rep"] },
  { to: "/nurture", label: "Nurture Calendar", icon: CalendarHeart, roles: ["admin", "supervisor", "rep"] },
  { to: "/mail", label: "Mail", icon: MailIcon, roles: ["admin", "supervisor", "rep"] },
  { to: "/allocation", label: "Allocation Queue", icon: Inbox, roles: ["admin", "supervisor"] },
  { to: "/opportunities", label: "Opportunities", icon: Radar, roles: ["admin", "supervisor"] },
  { to: "/linkedin", label: "LinkedIn Agent", icon: Linkedin, roles: ["admin", "supervisor", "rep"] },
  { to: "/accounts", label: "Accounts", icon: Building2, roles: ["admin", "supervisor", "rep"] },
  { to: "/vendors", label: "Vendor Directory", icon: Store, roles: ["admin", "supervisor", "rep"] },
  { to: "/network", label: "Warm Network", icon: Network, roles: ["admin", "supervisor", "rep"] },
  { to: "/import", label: "Bulk Import", icon: Upload, roles: ["admin", "supervisor", "rep"] },
  { to: "/coverage", label: "Rep Coverage", icon: CalendarClock, roles: ["admin", "supervisor"] },
  { to: "/knowledge", label: "Knowledge Base", icon: BookOpen, roles: ["admin", "supervisor"] },
  { to: "/offerings", label: "Offerings", icon: FileText, roles: ["admin", "supervisor"] },
  { to: "/regions", label: "Regional Config", icon: Globe, roles: ["admin", "supervisor"] },
  { to: "/spend", label: "Spend Meter", icon: Gauge, roles: ["admin"] },
  { to: "/audit", label: "Change Log", icon: ScrollText, roles: ["admin"] },
  { to: "/users", label: "User Management", icon: Users, roles: ["admin", "supervisor"] },
  { to: "/organization", label: "Organization & Roles", icon: ShieldCheck, roles: ["admin"] },
  { to: "/integrations/api-keys", label: "Integrations · API keys", icon: KeyRound, roles: ["admin"] },
  { to: "/integrations/jarvis", label: "Integrations · Jarvis", icon: Bot, roles: ["admin"] },
  { to: "/approvals", label: "Approvals", icon: CheckSquare, roles: ["admin", "supervisor", "rep"] },
  { to: "/offboarding", label: "Reassignment Queue", icon: UserMinus, roles: ["admin", "supervisor"] },
  { to: "/settings", label: "Approval Settings", icon: SlidersHorizontal, roles: ["admin", "supervisor", "rep"] },
  { to: "/help", label: "Help & Guides", icon: HelpCircle, roles: ["admin", "supervisor", "rep"] },
];

// Nav items gated by a non-core module — hidden when that module is disabled for the tenant.
const NAV_MODULE = { "/linkedin": "linkedin", "/vendors": "vendors", "/network": "warm_network", "/opportunities": "events", "/targets": "targets" };

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { theme, setTheme, collapsed, setCollapsed, textSize, setTextSize } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const nav = useNavigate();
  const [enabledMods, setEnabledMods] = useState(null);
  const [wizardDone, setWizardDone] = useState(null);
  useEffect(() => {
    if (user.role === "super_admin") return;
    api.get("/tenant/entitlements").then((r) => setEnabledMods(r.data.enabled_modules || null)).catch(() => {});
    if (user.role === "admin") api.get("/onboarding/wizard").then((r) => { setWizardDone(!!r.data.completed); if (!r.data.completed && window.location.pathname === "/") nav("/onboarding"); }).catch(() => setWizardDone(true));
  }, [user.role]);

  const modOk = (to) => { const m = NAV_MODULE[to]; return !m || !enabledMods || enabledMods.includes(m); };
  let items = user.role === "super_admin"
    ? [{ to: "/admin/tenants", label: "Tenants", icon: Building2 }, { to: "/help", label: "Help & Guides", icon: HelpCircle }]
    : NAV.filter((n) => n.roles.includes(user.role) && modOk(n.to));
  if (user.role === "admin" && wizardDone === false)
    items = [{ to: "/onboarding", label: "Setup Wizard", icon: Rocket }, ...items];

  const [pendingApprovals, setPendingApprovals] = useState(0);
  useEffect(() => {
    if (user.role === "super_admin") return;
    let alive = true;
    const load = () => api.get("/approvals/count").then((r) => { if (alive) setPendingApprovals(r.data.pending || 0); }).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [user.role]);

  return (
    <div className="min-h-screen flex bg-[#F5F3EE]">
      {mobileOpen && <div className="fixed inset-0 z-30 bg-black/50 md:hidden" onClick={() => setMobileOpen(false)} data-testid="sidebar-backdrop" />}
      <aside data-testid="app-sidebar" data-collapsed={collapsed ? "true" : "false"}
        className={`app-sidebar shrink-0 border-r border-[#DFDAD0] flex flex-col transition-[width,transform] duration-200
          fixed inset-y-0 left-0 z-40 md:static md:translate-x-0 ${mobileOpen ? "translate-x-0" : "-translate-x-full"} ${collapsed ? "md:w-16 w-60" : "w-60"}`}>
        <div className={`py-5 border-b border-[#DFDAD0] flex items-center ${collapsed ? "md:justify-center md:px-0 px-5 justify-between" : "px-5 justify-between"}`}>
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-7 h-7 shrink-0 bg-[#E50914] flex items-center justify-center rounded-sm shadow-[0_0_16px_rgba(229,9,20,0.5)]">
              <Radio className="w-4 h-4 text-white" strokeWidth={1.75} />
            </div>
            <div className={collapsed ? "md:hidden" : ""}>
              <div className="font-mono text-[13px] font-semibold leading-none text-white">VIS · AI SALES</div>
              <div className="font-mono text-[9px] tracking-[0.14em] text-white/50 mt-1">INNOVATIVE IDEAS</div>
            </div>
          </div>
          <button onClick={() => setMobileOpen(false)} className="md:hidden text-white/60 hover:text-white" data-testid="sidebar-close-btn"><X className="w-4 h-4" /></button>
        </div>
        <nav className="flex-1 py-2 overflow-y-auto hide-scrollbar">
          {items.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} title={n.label} onClick={() => setMobileOpen(false)}
              data-testid={`nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 py-2.5 text-sm border-l-2 transition-colors duration-150 ${collapsed ? "md:justify-center md:px-0 px-5" : "px-5"} ${
                  isActive
                    ? "border-[#E50914] bg-[#E50914]/10 text-white font-medium"
                    : "border-transparent text-white/55 hover:bg-white/5 hover:text-white"
                }`}>
              <n.icon className="w-4 h-4 shrink-0" strokeWidth={1.5} />
              <span className={`flex-1 ${collapsed ? "md:hidden" : ""}`}>{n.label}</span>
              {n.to === "/approvals" && pendingApprovals > 0 && (
                <span data-testid="approvals-badge"
                  className={`min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-[#E50914] text-white text-[10px] font-mono font-semibold ${collapsed ? "md:absolute md:ml-6 md:-mt-4" : ""}`}>
                  {pendingApprovals}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className={`border-t border-[#DFDAD0] p-4 ${collapsed ? "md:p-2" : ""}`}>
          <div className={collapsed ? "md:hidden" : ""}>
            <div className="font-mono text-[10px] uppercase tracking-wider text-white/50">{ROLE_LABEL[user.role] || user.role}</div>
            <div className="text-sm font-medium text-white truncate">{user.name}</div>
            <div className="text-xs text-white/50 truncate">{user.email}</div>
          </div>
          <button data-testid="logout-btn" onClick={() => { logout(); nav("/login"); }} title="Sign out"
            className={`mt-3 w-full flex items-center justify-center gap-2 border border-white/20 text-white/80 py-1.5 text-xs font-mono uppercase tracking-wider hover:bg-white/10 transition-colors ${collapsed ? "md:mt-0" : ""}`}>
            <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} /> <span className={collapsed ? "md:hidden" : ""}>Sign out</span>
          </button>
          <div className="md:hidden mt-2 space-y-2"><InstallAppButton /><PushToggleButton /></div>
          <button data-testid="sidebar-collapse-btn" onClick={() => setCollapsed(!collapsed)} title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="hidden md:flex mt-2 w-full items-center justify-center gap-2 text-white/40 hover:text-white py-1 text-[10px] font-mono uppercase tracking-wider">
            {collapsed ? <PanelLeftOpen className="w-4 h-4" /> : <><PanelLeftClose className="w-4 h-4" /> Collapse</>}
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0 overflow-x-hidden">
        <div className="glass-bar flex items-center justify-between md:justify-end gap-3 px-4 md:px-8 py-2 border-b border-[#DFDAD0] sticky top-0 z-20">
          <button onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn" className="md:hidden p-1.5 border border-[#DFDAD0] rounded-sm text-[#1C2530]/70"><Menu className="w-4 h-4" /></button>
          <div className="flex items-center gap-3">
            <button onClick={() => window.dispatchEvent(new KeyboardEvent("keydown", { key: "k", metaKey: true }))} data-testid="open-palette-btn"
              className="flex items-center gap-2 border border-[#DFDAD0] px-3 py-1.5 text-xs text-[#1C2530]/60 hover:bg-[#EFECE5] rounded-sm">
              <SearchIcon className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Search</span> <kbd className="hidden sm:inline font-mono text-[9px] border border-[#DFDAD0] px-1 rounded">⌘K</kbd>
            </button>
            <button onClick={() => setTheme(theme === "dark" ? "light" : "dark")} data-testid="theme-toggle-btn" title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              className="flex items-center gap-2 border border-[#DFDAD0] px-2.5 py-1.5 text-xs text-[#1C2530]/60 hover:bg-[#EFECE5] rounded-sm">
              {theme === "dark" ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
            </button>
            <div className="hidden sm:flex items-center border border-[#DFDAD0] rounded-sm overflow-hidden" data-testid="text-size-toggle" title="Text size">
              {[["compact", "A", "Compact"], ["comfortable", "A", "Comfortable"], ["large", "A", "Large"]].map(([k, l, t], i) => (
                <button key={k} onClick={() => setTextSize(k)} data-testid={`text-size-${k}`} title={`${t} text`} aria-pressed={textSize === k}
                  className={`px-2 py-1.5 font-mono ${i === 0 ? "text-[10px]" : i === 1 ? "text-xs" : "text-sm"} leading-none ${textSize === k ? "bg-[#1C2530] text-[#F5F3EE]" : "text-[#1C2530]/60 hover:bg-[#EFECE5]"}`}>{l}</button>
              ))}
            </div>
            <HelpButton />
            <NotificationTray />
          </div>
        </div>
        <CommandPalette />
        <WorkspaceBanners />
        {children}
      </main>
    </div>
  );
}

export function PageHeader({ eyebrow, title, right, hint }) {
  return (
    <div className="border-b border-[#DFDAD0] bg-white px-4 md:px-8 py-4 md:py-5">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div className="min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-[#1C2530]/60">{eyebrow}</div>
          <h1 className="text-xl md:text-2xl font-semibold tracking-tight text-[#1C2530] mt-1">{title}</h1>
        </div>
        {right && <div className="flex flex-wrap items-center gap-2 max-w-full">{right}</div>}
      </div>
      {hint && <p className="text-xs text-[#1C2530]/60 mt-2 max-w-3xl" data-testid="page-hint">{hint}</p>}
    </div>
  );
}

export function Empty({ label = "NO_DATA_FOUND" }) {
  return (
    <div className="border border-dashed border-[#DFDAD0] p-12 text-center font-mono text-sm text-[#1C2530]/50 tracking-wider">
      {label}
    </div>
  );
}

export function Loading({ label = "SYS_LOADING..." }) {
  return (
    <div className="p-12 text-center font-mono text-sm text-[#1C2530]/50 tracking-wider animate-pulse">
      {label}
    </div>
  );
}
